import { createFileRoute, notFound, Link } from '@tanstack/react-router'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { SafeImage } from '../../components/ui/SafeImage'
import { useState } from 'react'
import { Calendar, User, ArrowLeft, Share2, Heart } from 'lucide-react'
import { getPostBySlug, getAllPosts } from '../../lib/data/posts'
import type { Post } from '@pranala/shared'

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('id-ID', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function getDateString(date: Date | string | null | undefined): string | undefined {
  if (!date) return undefined
  return typeof date === 'string' ? date : date.toISOString()
}

export const Route = createFileRoute('/artikel/$slug')({
  component: ArtikelDetail,
  loader: async ({ params }) => {
    const post = getPostBySlug(params.slug)
    if (!post) {
      throw notFound()
    }
    
    // Get related posts (excluding current)
    const { posts: allPosts } = getAllPosts({ limit: 10 })
    const relatedPosts = allPosts
      .filter((p: Post) => p.id !== post.id)
      .slice(0, 3)
    
    return { post, relatedPosts }
  },
})

function ArtikelDetail() {
  const { post, relatedPosts } = Route.useLoaderData()
  const [isBookmarked, setIsBookmarked] = useState(false)

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt || '',
          url: window.location.href,
        })
      } catch (error) {
        console.error('Error sharing:', error)
      }
    } else {
      navigator.clipboard.writeText(window.location.href)
      alert('Link artikel disalin ke clipboard!')
    }
  }

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked)
  }

  return (
    <div className="min-h-screen bg-brand-slate-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-brand-slate-200">
        <Container>
          <nav className="flex items-center space-x-2 py-4 text-sm">
            <Link to="/" className="text-brand-slate-500 hover:text-brand-yellow-600">
              Beranda
            </Link>
            <span className="text-brand-slate-400">/</span>
            <Link to="/artikel" className="text-brand-slate-500 hover:text-brand-yellow-600">
              Artikel
            </Link>
            <span className="text-brand-slate-400">/</span>
            <span className="text-brand-slate-900 truncate">{post.title}</span>
          </nav>
        </Container>
      </div>

      <Container className="py-8 max-w-7xl mx-auto">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/artikel"
            className="inline-flex items-center text-brand-slate-600 hover:text-brand-yellow-600 mb-6 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Kembali ke Daftar Artikel
          </Link>

          {/* Article Header */}
          <article className="bg-white border border-brand-slate-200 rounded-xl overflow-hidden">
            {/* Cover Image */}
            {post.featuredImage && (
              <div className="aspect-video bg-brand-slate-200 overflow-hidden">
                <SafeImage
                  src={post.featuredImage}
                  alt={post.title}
                  width={800}
                  height={450}
                  className="w-full h-full object-cover"
                  fallback="placeholder"
                />
              </div>
            )}

            <div className="p-6 md:p-8">
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center text-brand-slate-500 text-sm">
                  <Calendar className="w-4 h-4 mr-1 text-yellow-600" />
                  <time dateTime={getDateString(post.publishedAt || post.createdAt)}>
                    {formatDate(post.publishedAt || post.createdAt)}
                  </time>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl text-brand-slate-900 font-bold tracking-tight mb-4">
                {post.title}
              </h1>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-xl text-brand-slate-600 mb-6 leading-relaxed">{post.excerpt}</p>
              )}

              {/* Author and Actions */}
              <div className="flex items-center justify-between pb-6 border-b border-brand-slate-200 mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    <User className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-medium text-brand-slate-900">Admin</p>
                    <p className="text-sm text-brand-slate-500">Penulis</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShare}
                    className="flex items-center gap-2 border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600"
                  >
                    <Share2 className="w-4 h-4" />
                    Bagikan
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBookmark}
                    className={`flex items-center gap-2 ${isBookmarked ? 'text-brand-yellow-600 border-brand-yellow-400 bg-brand-yellow-50' : 'border-brand-slate-200 hover:border-brand-yellow-400 hover:text-brand-yellow-600'}`}
                  >
                    <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
                    {isBookmarked ? 'Disimpan' : 'Simpan'}
                  </Button>
                </div>
              </div>

              {/* Article Content */}
              <div
                className="prose prose-lg max-w-none prose-headings:text-brand-slate-900 prose-p:text-brand-slate-700 prose-a:text-brand-yellow-600 prose-a:no-underline hover:prose-a:underline prose-strong:text-brand-slate-900 prose-code:text-brand-yellow-600 prose-pre:bg-brand-slate-50 prose-blockquote:border-l-4 prose-blockquote:border-brand-yellow-400 prose-blockquote:bg-brand-yellow-50 prose-blockquote:p-4 prose-blockquote:italic"
                dangerouslySetInnerHTML={{ __html: post.content || '' }}
              />
            </div>
          </article>

          {/* Related Articles */}
          {relatedPosts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl text-brand-slate-900 tracking-tight mb-6">
                Artikel Terkait
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedPosts.map((related: Post) => (
                  <div
                    key={related.id}
                    className="bg-white border border-brand-slate-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-brand-yellow-400 transition-all duration-300 group"
                  >
                    {related.featuredImage && (
                      <div className="h-32 bg-brand-slate-200 overflow-hidden">
                        <SafeImage
                          src={related.featuredImage}
                          alt={related.title}
                          width={400}
                          height={200}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          fallback="placeholder"
                        />
                      </div>
                    )}

                    <div className="p-4">
                      <h3 className="text-brand-slate-900 mb-2 line-clamp-2 group-hover:text-brand-yellow-600 transition-colors">
                        <Link
                          to="/artikel/$slug"
                          params={{ slug: related.slug }}
                          className="hover:text-yellow-600 transition-colors"
                        >
                          {related.title}
                        </Link>
                      </h3>

                      {related.excerpt && (
                        <p className="text-sm text-brand-slate-500 mb-3 line-clamp-2">{related.excerpt}</p>
                      )}

                      <div className="flex items-center justify-between text-xs text-brand-slate-400">
                        <time dateTime={getDateString(related.publishedAt || related.createdAt)}>
                          {formatDate(related.publishedAt || related.createdAt)}
                        </time>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Container>
    </div>
  )
}
